import React from 'react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  useTheme,
  Box,
  Typography,
  LinearProgress,
  Tooltip
} from '@mui/material';
import { tokens } from '../../../theme';
import { WarningAmber } from '@mui/icons-material';

const LowStockTable = ({ products }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // Empty state
  if (products.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="300px">
        <Typography color={colors.grey[500]}>No low stock products found</Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} sx={{ bgcolor: 'transparent', maxHeight: '400px' }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ color: colors.grey[100], fontWeight: 'bold' }}>Product</TableCell>
            <TableCell sx={{ color: colors.grey[100], fontWeight: 'bold' }}>SKU</TableCell>
            <TableCell sx={{ color: colors.grey[100], fontWeight: 'bold' }}>Current Stock</TableCell>
            <TableCell sx={{ color: colors.grey[100], fontWeight: 'bold' }}>Min Level</TableCell>
            <TableCell sx={{ color: colors.grey[100], fontWeight: 'bold' }}>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {products.map((product) => {
            // Calculate stock level percentage
            const stockPercentage = Math.min(
              Math.max(0, (product.stockQuantity / product.minStockLevel) * 100),
              100
            );
            
            // Determine color based on stock level
            let stockColor;
            if (stockPercentage <= 25) {
              stockColor = colors.redAccent[500];
            } else if (stockPercentage <= 75) {
              stockColor = colors.orangeAccent[500];
            } else {
              stockColor = colors.greenAccent[500];
            }
            
            return (
              <TableRow
                key={product._id}
                sx={{ 
                  '&:hover': { bgcolor: colors.primary[500] },
                  cursor: 'pointer'
                }}
              >
                <TableCell>
                  <Typography color={colors.grey[100]}>
                    {product.name}
                  </Typography>
                  <Typography variant="body2" color={colors.grey[400]}>
                    {product.materialType?.name || 'No category'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography color={colors.grey[300]}>
                    {product.artikelNummer}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography color={colors.grey[100]}>
                    {product.stockQuantity} {product.unitName}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography color={colors.grey[100]}>
                    {product.minStockLevel} {product.unitName}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ width: '100%' }}>
                    <Box display="flex" alignItems="center" mb={0.5}>
                      <Typography variant="body2" color={colors.grey[100]} sx={{ mr: 1 }}>
                        {stockPercentage.toFixed(0)}%
                      </Typography>
                      {stockPercentage <= 25 && (
                        <Tooltip title="Critical Low Stock">
                          <WarningAmber fontSize="small" sx={{ color: colors.redAccent[500] }} />
                        </Tooltip>
                      )}
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={stockPercentage}
                      sx={{
                        height: 8,
                        borderRadius: 5,
                        bgcolor: colors.grey[800],
                        '& .MuiLinearProgress-bar': {
                          bgcolor: stockColor,
                        },
                      }}
                    />
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default LowStockTable;